import posthog from "posthog-js";
import { useAuthStore } from "@/store/authStore";

// Constants
const POSTHOG_KEY = "phc_sbAp3TSf9qrZFqZSBDkQ5nXOkOhOD4FgZpfVElPNQbx";
const POSTHOG_HOST = "https://eu.i.posthog.com";

// Feature flag types
export type FeatureFlags = {
  [key: string]: boolean | string | number;
};

// Initialize PostHog
export const initAnalytics = () => {
  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: true,
      loaded: (posthog) => {
        // Identify user if logged in
        const user = useAuthStore.getState().user;
        if (user) {
          identifyUser(user.id, {
            name: user.first_name + " " + user.last_name,
            email: user.email,
            role: user.role,
          });
        }
      },
    });
  } catch (error) {
    console.error("Failed to initialize PostHog:", error);
  }
};

// User identification
export const identifyUser = (
  userId: string,
  properties?: Record<string, unknown>
) => {
  try {
    posthog.identify(userId, properties);
  } catch (error) {
    console.error("Failed to identify user in analytics:", error);
  }
};

// General event tracking
export const trackEvent = (event: string, properties?: Record<string, unknown>) => {
  try {
    posthog.capture(event, properties);
  } catch (error) {
    console.error(`Failed to track event ${event}:`, error);
  }
};

// Page view tracking
export const trackPageView = (
  pageName: string,
  properties?: Record<string, unknown>
) => {
  try {
    posthog.capture("$pageview", {
      page: pageName,
      ...properties,
    });
  } catch (error) {
    console.error(`Failed to track page view ${pageName}:`, error);
  }
};

// Feature flag management
export const getFeatureFlag = (key: string, defaultValue: unknown = false): unknown => {
  try {
    return posthog.getFeatureFlag(key) ?? defaultValue;
  } catch (error) {
    console.error(`Failed to get feature flag ${key}:`, error);
    return defaultValue;
  }
};

// Check if feature is enabled
export const isFeatureEnabled = (
  key: string,
  defaultValue = false
): boolean => {
  try {
    return posthog.isFeatureEnabled(key) ?? defaultValue;
  } catch (error) {
    console.error(`Failed to check feature flag ${key}:`, error);
    return defaultValue;
  }
};

// Get all feature flags for current user
export const getAllFeatureFlags = (): FeatureFlags => {
  try {
    // The error was here - 'getFeatureFlags()' doesn't exist in PostHog
    // Instead, we need to create a function that collects all flags
    const allFlags: FeatureFlags = {};
    // We don't have a direct API to get all flags, so we'd need to know the keys or use a different approach
    // For now, return an empty object or implement a custom approach to gather known flags
    return allFlags;
  } catch (error) {
    console.error("Failed to get all feature flags:", error);
    return {};
  }
};

// Reset user identity (for logout)
export const resetAnalyticsUser = () => {
  try {
    posthog.reset();
  } catch (error) {
    console.error("Failed to reset analytics user:", error);
  }
};

// Event trackers for specific application events
export const analyticsEvents = {
  // Make trackEvent and trackPageView directly accessible
  trackEvent,
  trackPageView,

  // Auth events
  auth: {
    login: (method: string = "email") => trackEvent("user_login", { method }),
    logout: () => trackEvent("user_logout"),
    register: (method: string = "email") =>
      trackEvent("user_register", { method }),
    passwordReset: () => trackEvent("password_reset_request"),
  },

  // Store events
  store: {
    create: (storeId: string, storeName: string) =>
      trackEvent("store_created", { store_id: storeId, store_name: storeName }),
    update: (storeId: string, storeName: string) =>
      trackEvent("store_updated", { store_id: storeId, store_name: storeName }),
    delete: (storeId: string, storeName: string) =>
      trackEvent("store_deleted", { store_id: storeId, store_name: storeName }),
  },

  // Menu events
  menu: {
    itemCreated: (itemId: string, itemName: string, category: string) =>
      trackEvent("menu_item_created", {
        item_id: itemId,
        item_name: itemName,
        category,
      }),
    itemUpdated: (itemId: string, itemName: string) =>
      trackEvent("menu_item_updated", { item_id: itemId, item_name: itemName }),
    itemDeleted: (itemId: string, itemName: string) =>
      trackEvent("menu_item_deleted", { item_id: itemId, item_name: itemName }),
  },

  // Order events
  order: {
    created: (orderId: string, total: number, items: number) =>
      trackEvent("order_created", {
        order_id: orderId,
        total,
        item_count: items,
      }),
    updated: (orderId: string, status: string) =>
      trackEvent("order_updated", { order_id: orderId, status }),
    completed: (orderId: string, total: number) =>
      trackEvent("order_completed", { order_id: orderId, total }),
    cancelled: (orderId: string, reason?: string) =>
      trackEvent("order_cancelled", { order_id: orderId, reason }),
  },

  // Chat events
  chat: {
    messageSent: (storeId: string, messageType: string) =>
      trackEvent("chat_message_sent", {
        store_id: storeId,
        message_type: messageType,
      }),
    messageReceived: (storeId: string) =>
      trackEvent("chat_message_received", { store_id: storeId }),
    reactionAdded: (messageId: string, emoji: string) =>
      trackEvent("chat_reaction_added", { message_id: messageId, emoji }),
  },

  // Staff events
  staff: {
    invited: (role: string) => trackEvent("staff_invited", { role }),
    added: (role: string) => trackEvent("staff_added", { role }),
    removed: (role: string) => trackEvent("staff_removed", { role }),
    roleChanged: (fromRole: string, toRole: string) =>
      trackEvent("staff_role_changed", {
        from_role: fromRole,
        to_role: toRole,
      }),
  },

  // Settings events
  settings: {
    updated: (settingType: string) =>
      trackEvent("settings_updated", { setting_type: settingType }),
    preferenceChanged: (preference: string, value: string) =>
      trackEvent("preference_changed", { preference, value }),
  },

  // Inventory events
  inventory: {
    itemCreated: (
      itemId: string,
      itemName: string,
      storeId: string,
      inventoryType: string
    ) =>
      trackEvent("inventory_item_created", {
        item_id: itemId,
        item_name: itemName,
        store_id: storeId,
        inventory_type: inventoryType,
      }),
    itemUpdated: (itemId: string, itemName: string, storeId: string) =>
      trackEvent("inventory_item_updated", {
        item_id: itemId,
        item_name: itemName,
        store_id: storeId,
      }),
    itemDeleted: (itemId: string, itemName: string, storeId: string) =>
      trackEvent("inventory_item_deleted", {
        item_id: itemId,
        item_name: itemName,
        store_id: storeId,
      }),
    quantityUpdated: (
      itemId: string,
      itemName: string,
      oldQuantity: number,
      newQuantity: number,
      storeId: string
    ) =>
      trackEvent("inventory_quantity_updated", {
        item_id: itemId,
        item_name: itemName,
        old_quantity: oldQuantity,
        new_quantity: newQuantity,
        store_id: storeId,
      }),
    modeToggled: (storeId: string, enabled: boolean) =>
      trackEvent("inventory_mode_toggled", {
        store_id: storeId,
        advanced_mode: enabled,
      }),
    menuItemLinked: (
      inventoryItemId: string,
      menuItemId: string,
      storeId: string
    ) =>
      trackEvent("inventory_menu_item_linked", {
        inventory_item_id: inventoryItemId,
        menu_item_id: menuItemId,
        store_id: storeId,
      }),
    stockAlert: (
      itemId: string,
      itemName: string,
      currentQuantity: number,
      minQuantity: number,
      storeId: string
    ) =>
      trackEvent("inventory_stock_alert", {
        item_id: itemId,
        item_name: itemName,
        current_quantity: currentQuantity,
        minimum_quantity: minQuantity,
        store_id: storeId,
        alert_type: currentQuantity <= 0 ? "out_of_stock" : "low_stock",
      }),
  },
};
